import { useEffect, useMemo, useRef, useState } from "react";

import { getWorkspace } from "../../../services/workspacesApi";
import type { WorkspaceAccountScope } from "../../../types/workspaceAccountScope";
import type { WorkspaceDetail, WorkspaceSummary } from "../../../types/workspace";
import {
  toWorkspaceCanvasPreviewGraph,
  type WorkspaceCanvasPreviewGraph,
} from "./WorkspaceCanvasPreview";

const FETCH_CONCURRENCY = 3;

export type WorkspacePreviewEntry =
  | { status: "loading" }
  | { status: "ready"; graph: WorkspaceCanvasPreviewGraph }
  | { status: "error" };

function workspacesPreviewKey(workspaces: WorkspaceSummary[]): string {
  return workspaces
    .map((w) => `${w.id}:${w.updatedAt ?? ""}:${w.nodeCount ?? ""}`)
    .join(",");
}

/**
 * Lazily loads each workspace’s saved graph so hub cards can show a real layout snapshot.
 */
export function useWorkspaceCanvasPreviews(
  scope: WorkspaceAccountScope | null,
  workspaces: WorkspaceSummary[]
): {
  previews: Record<string, WorkspacePreviewEntry>;
  seedPreview: (detail: WorkspaceDetail) => void;
} {
  const [previews, setPreviews] = useState<Record<string, WorkspacePreviewEntry>>({});
  const cacheKeyRef = useRef("");
  const inFlightRef = useRef(new Set<string>());
  const workspacesKey = useMemo(() => workspacesPreviewKey(workspaces), [workspaces]);

  const seedPreview = (detail: WorkspaceDetail) => {
    setPreviews((prev) => ({
      ...prev,
      [detail.id]: {
        status: "ready",
        graph: toWorkspaceCanvasPreviewGraph(detail),
      },
    }));
  };

  useEffect(() => {
    if (!scope || workspaces.length === 0) {
      setPreviews({});
      cacheKeyRef.current = "";
      return;
    }

    const scopeKey = `${scope.brokerName}|${scope.brokerAccountCode}`;
    const nextKey = `${scopeKey}::${workspacesKey}`;
    if (nextKey === cacheKeyRef.current) return;
    cacheKeyRef.current = nextKey;

    let cancelled = false;
    const queue = [...workspaces];

    setPreviews((prev) => {
      const merged: Record<string, WorkspacePreviewEntry> = {};
      for (const workspace of workspaces) {
        const existing = prev[workspace.id];
        merged[workspace.id] =
          existing?.status === "ready" ? existing : { status: "loading" };
      }
      return merged;
    });

    const runWorker = async () => {
      while (!cancelled) {
        const workspace = queue.shift();
        if (!workspace) return;
        if (inFlightRef.current.has(workspace.id)) continue;
        inFlightRef.current.add(workspace.id);
        try {
          const detail = await getWorkspace(scope, workspace.id);
          if (cancelled) return;
          setPreviews((prev) => ({
            ...prev,
            [workspace.id]: {
              status: "ready",
              graph: toWorkspaceCanvasPreviewGraph(detail),
            },
          }));
        } catch {
          if (cancelled) return;
          setPreviews((prev) => ({
            ...prev,
            [workspace.id]: { status: "error" },
          }));
        } finally {
          inFlightRef.current.delete(workspace.id);
        }
      }
    };

    void Promise.all(
      Array.from({ length: Math.min(FETCH_CONCURRENCY, queue.length) }, () => runWorker())
    );

    return () => {
      cancelled = true;
      // Allow React Strict Mode remount (or scope bounce) to refetch.
      if (cacheKeyRef.current === nextKey) {
        cacheKeyRef.current = "";
      }
    };
  }, [scope, workspaces, workspacesKey]);

  return { previews, seedPreview };
}
